import React, { ReactNode } from 'react';
import { History } from 'history';
import { PaymentMethodType } from '@ordermentum/ui-types/src/purchaser';
import { Retailer } from '@ordermentum/ui-types';
import { ToastReduxAction } from '@ordermentum/ui-definitions';
import NewCreditCard from './NewCreditCard';

import { ThemeContext } from '../../utilities/theme';

import Icon from '../../foundations/Icons';
import { DirectDebitRequest } from '../../components/PaymentMethods';
import { DirectDebitParams } from '../../redux/payment_methods';

function LinkText({
  children,
  ...rest
}: React.HTMLProps<HTMLAnchorElement> & { children: ReactNode }) {
  return (
    <a
      className="cursor-pointer font-body text-label-small-bold text-primary-brand hover:underline"
      {...rest}
    >
      {children}
    </a>
  );
}

function Title({ children }: { children: ReactNode }) {
  return <h3 className="text-center">{children}</h3>;
}

function PaymentMethod({ children }: { children: ReactNode }) {
  return (
    <div className="relative mt-xxxl flex w-full flex-col bg-background-card before:absolute before:left-m before:top-m before:font-body before:text-body">
      {children}
    </div>
  );
}

type Props = {
  params: {
    retailerId: string;
    supplierId?: string;
    paymentRequestId?: string;
  };
  type: PaymentMethodType;
  location: any;
  cancelUrl?: string;
  history: History;
  paymentType?: Array<string>;
  purchaserId?: string;
  retailer?: Retailer;
  fetchPurchaserAsync?: (supplierId: string, retailerId: string) => void;
  onSuccess?: (...args) => void;
  showAuthority?: boolean;
  showBasicEncryption?: boolean;
  title?: boolean;
  source?: string;
  confirmModal?: {
    text: string;
    confirmLabel: string;
  };
  addDirectPayment: (
    directDetails: DirectDebitParams,
    retailerId: string
  ) => Promise<void>;
  showToast: (toast: ToastReduxAction) => Promise<void>;
};

export const NewPaymentMethod = (props: Props) => {
  const {
    type,
    params,
    location,
    history,
    fetchPurchaserAsync,
    cancelUrl,
    paymentType,
    purchaserId,
    addDirectPayment,
    retailer,
    onSuccess,
    showAuthority,
    showBasicEncryption,
    confirmModal,
    source = '',
    title,
    showToast,
  } = props;

  const { retailerId } = params;

  return (
    <ThemeContext.Provider value="futuro">
      {type === 'card' && (
        <PaymentMethod>
          {title && <Title>Credit / Debit Card</Title>}
          <NewCreditCard
            params={params}
            location={location}
            paymentType={paymentType ?? []}
            fetchPurchaserDetailsAsync={fetchPurchaserAsync}
            history={history}
            cancelUrl={cancelUrl}
            purchaserId={purchaserId}
            onSuccess={onSuccess}
            confirmModal={confirmModal}
            source={source}
          />
          {showBasicEncryption && (
            <div className="flex items-center justify-center pb-xl">
              <Icon name="lLock" />
              Payments are proccessed securely using 128-bit encryption
            </div>
          )}
        </PaymentMethod>
      )}
      {type === 'direct' && (
        <div className="pt-xxl">
          <DirectDebitRequest
            onFailedValidation={showToast}
            title="Direct Debit Request"
            retailerId={retailerId}
            retailer={retailer}
            onSuccess={onSuccess}
            addDirectPayment={addDirectPayment}
            isPaymentLink
          />
        </div>
      )}
      {showAuthority && type !== 'direct' && (
        <div className="flex gap-s p-s bg-special">
          <Icon name="lLock" />
          <p className="font-body text-label-small-bold text-regular">
            Financial information and payments are securely encrypted before
            being stored.
            <br />
            <br />
            By saving your payment method you are giving Ordermentum authority
            to pay invoices on their due date using this card.
            <br />
            <br />
            By using Ordermentum services, you agree to the Ordermentum{' '}
            <LinkText
              href="https://www.ordermentum.com/terms-conditions"
              target="_blank"
              rel="noopener noreferrer"
            >
              Terms and conditions
            </LinkText>{' '}
            and{' '}
            <LinkText
              href="https://www.ordermentum.com/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
            >
              Privacy policy.
            </LinkText>
          </p>
        </div>
      )}
    </ThemeContext.Provider>
  );
};

export default NewPaymentMethod;
